/**
 * The rules for per-template price overrides.
 *
 * Absolute prices layered over the base catalog, scoped to a template. The
 * server resolves COALESCE(template, trim, base) — this module is only about
 * AUTHORING them: which cell is overridden, which cell shadows a trim price,
 * what a bulk adjustment produces, and what to send on save.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/templateOverrides.test.mjs`. Do not add imports; in particular
 * not catalogValidation, which pulls in axios and cannot load under node:test.
 */

const KEY_SEP = '|';

/** Stable, parseable cell identity: master service × size × catalog. */
export function cellKey(masterServiceId, size, catalog) {
  return [
    String(masterServiceId),
    String(size || '').trim().toUpperCase(),
    String(catalog || '').trim().toUpperCase(),
  ].join(KEY_SEP);
}

/** The inverse of cellKey. The id comes back as a Number, as the API sends it. */
export function parseCellKey(key) {
  const [id, size, catalog] = String(key).split(KEY_SEP);
  return { masterServiceId: Number(id), size, catalog };
}

/**
 * Server override rows → Map<cellKey, price>.
 *
 * A row missing any part of its key is dropped rather than indexed under a
 * malformed key, where it would be invisible to the grid but still round-trip
 * on save.
 */
export function indexOverrides(rows) {
  const out = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r) return;
    const id = r.master_service_id ?? r.masterServiceId;
    const size = r.vehicle_size ?? r.vehicleSize;
    const catalog = r.catalog;
    const price = r.override_price ?? r.overridePrice;
    if (id == null || !size || !catalog || price == null) return;
    const n = Number(price);
    if (!Number.isFinite(n)) return;
    out.set(cellKey(id, size, catalog), n);
  });
  return out;
}

/**
 * What a cell shows.
 *
 * `override` is undefined when absent — NOT null and NOT zero. `0` is a real
 * price an installer can set, so the test is on presence, never truthiness.
 */
export function resolveCell(base, override) {
  if (override === undefined || override === null) {
    return { value: base === undefined ? null : base, isOverride: false };
  }
  return { value: override, isOverride: true };
}

const shadowKey = (masterServiceId, size) =>
  `${masterServiceId}${KEY_SEP}${String(size || '').trim().toUpperCase()}`;

/**
 * Which cells sit on top of a vehicle-specific price.
 *
 * Template overrides WIN over trim overrides (spec D5), resolved per cell
 * (D6) — so typing into a cell that has trim prices underneath silently stops
 * them applying for every dealership on this template. This index is what lets
 * the grid say so (D7).
 *
 * Keyed by (service, size) and NOT by catalog: getAllTrimOverrides returns one
 * row per (service, size, trim) carrying retail_price and wholesale_price
 * together, so a trim override shadows both catalogs at once.
 */
export function indexTrimShadows(trimRows) {
  const out = new Map();
  const seen = new Set();
  (Array.isArray(trimRows) ? trimRows : []).forEach((o) => {
    if (!o) return;
    const msId = o.master_service_id ?? o.masterServiceId;
    const size = o.vehicle_size ?? o.vehicleSize;
    const trimId = o.id_car_trim ?? o.trimId;
    if (msId == null || !size || trimId == null) return;

    const key = shadowKey(msId, size);
    const dedupe = `${key}${KEY_SEP}${trimId}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);

    const label = [o.make_name, o.model_name, o.trim_name]
      .filter(Boolean).join(' ').trim() || `Vehicle ${trimId}`;

    if (!out.has(key)) out.set(key, []);
    out.get(key).push({ trimId, label });
  });
  return out;
}

/** The shadows for one cell's (service, size). Always an array. */
export function shadowsFor(shadowIndex, masterServiceId, size) {
  if (!shadowIndex) return [];
  return shadowIndex.get(shadowKey(masterServiceId, size)) || [];
}

/**
 * What to send on save.
 *
 * Clearing a cell produces a DELETE, never a write of 0 or null — absent row
 * means inherit, and `0` is a legitimate price. Conflating them would price a
 * service at zero for every dealership on the template.
 */
export function diffOverrides(loaded, edited) {
  const before = loaded || new Map();
  const after = edited || new Map();
  const upserts = [];
  const deletes = [];

  after.forEach((price, key) => {
    if (before.has(key) && before.get(key) === price) return;
    const { masterServiceId, size, catalog } = parseCellKey(key);
    upserts.push({
      master_service_id: masterServiceId,
      vehicle_size: size,
      catalog,
      override_price: price,
    });
  });

  before.forEach((_price, key) => {
    if (after.has(key)) return;
    const { masterServiceId, size, catalog } = parseCellKey(key);
    deletes.push({ master_service_id: masterServiceId, vehicle_size: size, catalog });
  });

  return { upserts, deletes };
}
